import * as React from 'react';
import { useState, useCallback } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Container, Typography, Box } from '@mui/material';
import { blue, teal } from '@mui/material/colors';
import CalculatorForm from './components/CalculatorForm';
import CalculationResult from './components/CalculationResult';
import { 
  CalculationResult as CalculationResultType, 
  CalculationParams, 
  ProductCategory, 
  ShippingMode, 
  InternationalShippingRule 
} from './types';
import { 
  calculateInternationalShipping,
  InternationalShippingResult as ShippingResult
} from './utils/calculations';
import { calculateCommission, calculateCommissionRate } from './utils/calculateCommission';

// 创建主题
const theme = createTheme({
  palette: {
    primary: {
      main: blue[700],
    },
    secondary: {
      main: teal[500],
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
  },
});

const App: React.FC = () => {
  const [result, setResult] = useState<CalculationResultType | null>(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = useCallback(async (data: CalculationParams) => {
    setLoading(true);
    
    // 模拟API调用延迟
    const timer = window.setTimeout(async () => {
      try {
        // 从输入数据中解构参数
        const { 
          purchasePrice: purchasePriceInput, 
          weight: weightInput, 
          dimensions: dims,
          category,
          shippingMode,
          finalSellingPrice: finalSellingPriceInput
        } = data;
        
        // 确保尺寸有有效值
        const dimensions = {
          length: dims?.length || 10,
          width: dims?.width || 10,
          height: dims?.height || 10
        };
        
        // 确保重量是有效数字
        const weight = Number(weightInput) || 0;
        const purchasePrice = Number(purchasePriceInput) || 0;
        const finalSellingPrice = Number(finalSellingPriceInput) || 0;
        
        // 计算国内运费：首重15元，每增加1000克加6元
        const baseWeight = 1000; // 首重1000克
        const basePrice = 15;    // 首重价格15元
        const additionalPrice = 6; // 每增加1000克加6元
        
        // 计算超出首重的重量（向上取整）
        const additionalWeight = Math.max(0, weight - baseWeight);
        const additionalUnits = Math.ceil(additionalWeight / 1000);
        
        const domesticShipping = basePrice + (additionalUnits * additionalPrice);
        
        // 计算国际运费
        // 注意：这里需要一个预估的卢布价格来计算国际运费
        // 由于这是一个循环依赖（售价影响国际运费，国际运费又影响售价）
        // 我们这里先使用一个估算值，实际实现中可能需要迭代计算
        const estimatedPriceInRubForShipping = purchasePrice * 12; // 假设汇率为1:12
        
        let internationalShipping: number = 0;
        // 计算国际运费
        const priceInRub = finalSellingPrice * 12.5; // 转换为卢布
        console.log('计算国际运费:', {
          价格: finalSellingPrice + ' CNY',
          卢布价格: priceInRub + ' RUB',
          重量: weight + 'g',
          尺寸: dimensions
        });
        
        // 获取国际运费规则描述
        const ruleDescriptions = [
          '1克→500克, 不超1500卢布',
          '501克→25千克, 不超1500卢布',
          '1501→7000卢布, 1克→2千克',
          '1501→7000卢布, 2001克→25千克',
          '7001→250000卢布, 1克→5千克',
          '7001→250000卢布, 5001克→25千克'
        ];
        
        // 初始化国际运费和规则
        let internationalShippingValue = 0;
        let internationalShippingRule: InternationalShippingRule | null = null;
        
        try {
          console.log('计算国际运费参数:', { 
            priceInRub, 
            weight, 
            dimensions,
            priceInRubType: typeof priceInRub,
            weightType: typeof weight
          });
          
          // Ensure parameters are numbers
          const validPriceInRub = Number(priceInRub) || 0;
          const validWeight = Number(weight) || 0;
          
          const shippingResult: ShippingResult = calculateInternationalShipping(
            validPriceInRub,
            validWeight,
            dimensions
          );
          
          if (shippingResult && typeof shippingResult === 'object' && 'cost' in shippingResult) {
            internationalShippingValue = Number(shippingResult.cost) || 0;
            
            if ('rule' in shippingResult && shippingResult.rule && typeof shippingResult.rule === 'object') {
              // 根据规则生成描述
              const rule = shippingResult.rule;
              const description = `价格: ${rule.minPrice}-${rule.maxPrice} RUB, 重量: ${rule.minWeight}-${rule.maxWeight}g`;
              
              internationalShippingRule = {
                ...rule,
                description
              };
              
              console.log('国际运费计算结果:', {
                cost: internationalShippingValue + ' CNY',
                rule: description,
                baseFee: rule.baseFee + ' CNY',
                ratePerGram: rule.ratePerGram + ' CNY/g',
                maxDimension: rule.maxDimension || '无限制',
                maxGirth: rule.maxGirth
              });
            }
          } else {
            throw new Error('无效的国际运费计算结果');
          }
        } catch (error) {
          console.error('计算国际运费时出错:', error);
          // 设置一个明显的错误值，方便调试
          internationalShippingValue = -1;
          internationalShippingRule = {
            minPrice: 0,
            maxPrice: 0,
            minWeight: 0,
            maxWeight: 0,
            baseFee: 0,
            ratePerGram: 0,
            maxGirth: 0,
            description: '计算错误: ' + (error instanceof Error ? error.message : '未知错误')
          };
        }
        
        // 计算国内和国际运费总和
        const shippingCost = domesticShipping + internationalShippingValue;
        
        // 计算平台佣金
        const estimatedPriceInRub = purchasePrice * 12; // 假设1 CNY = 12 RUB
        const platformCommission = calculateCommission(
          estimatedPriceInRub,
          category,
          shippingMode
        );
        
        // 计算总成本
        const totalCost = purchasePrice + shippingCost + platformCommission;
        
        // 使用用户输入的最终售价
        const suggestedPrice = finalSellingPrice;
        
        // 计算利润
        const profit = suggestedPrice - totalCost;
        const profitRate = (profit / suggestedPrice) * 100;
        
        // 创建结果对象
        const result: CalculationResultType = {
          purchasePrice,
          weight,
          dimensions,
          category,
          shippingMode,
          finalSellingPrice,
          domesticShipping,
          internationalShipping: internationalShippingValue,
          internationalShippingRule: internationalShippingRule || undefined,
          platformCommission,
          platformCommissionRate: calculateCommissionRate(estimatedPriceInRub, category, shippingMode),
          totalCost,
          suggestedPrice,
          suggestedPriceRub: suggestedPrice * 12.5, // Convert RMB to RUB using exchange rate
          finalPriceRub: suggestedPrice * 12.5, // Same as suggestedPriceRub for backward compatibility
          profit,
          profitRate: parseFloat(profitRate.toFixed(2))
        };
        
        setResult(result);
      } catch (error) {
        console.error('计算错误:', error);
      } finally {
        setLoading(false);
      }
    }, 500); // 减少延迟时间
    
    
    // 清理函数
    return () => {
      window.clearTimeout(timer);
    };
  }, []); // 空依赖数组，因为这个回调不依赖于任何外部变量

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', py: 4, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" component="h1" gutterBottom>
              Ozon 计算器
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              计算您的商品在 Ozon 平台上的成本、售价和利润
            </Typography>
          </Box>
          
          <CalculatorForm onCalculate={handleCalculate} loading={loading} />
          
          {result && (
            <Box mt={4}>
              <CalculationResult result={result} loading={loading} />
            </Box>
          )}
          
          <Box mt={4} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              © {new Date().getFullYear()} Ozon 成本计算器 - 本工具仅供参考，实际费用可能因市场波动而有所不同
            </Typography>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default App;
